// Stacking-order coordination for the overlay primitives in this folder
// (`Modal`, `SlideOutPanel`, `FullScreenModal`, and `ConfirmDialog`, which
// wraps `Modal`). Also the one owner of how a CLOSED
// eager overlay leaves the accessibility tree (`closedOverlayA11yProps`, BL-1959).
//
// Why this exists (BL-909): every overlay primitive defaulted to a flat
// `zIndex = 50`, so an overlay opened from *inside* another overlay had no way
// to know what it was opening on top of. `LotPanel` renders its slide-out at
// `zIndex={51}` — a deliberate one-off bump — and the `AddCustomerModal` opened
// from the panel's Seller field therefore rendered at 50, i.e. BEHIND the panel
// it was launched from. QA found the Business toggle and Save button obscured
// and the form impossible to complete. `AddSellerModal`, opened from the same
// panel, had the identical trap.
//
// Patching `zIndex={52}` onto those two call sites would have fixed the two
// modals QA happened to open and left the trap armed for the next one, because
// the number a nested overlay needs is a property of its *context*, not of
// itself. So the context is what we pass: each overlay publishes the layer it
// occupies to its React subtree, and a nested overlay derives its own layer
// from that instead of assuming it is the first one on screen.
//
// React context crosses portals (a portal moves DOM nodes, not the React tree),
// so this works even though every overlay portals to `document.body` and the
// nested overlay is a DOM *sibling* of its parent rather than a descendant.
//
// An explicit `zIndex` prop still wins, but it is a NAMED VARIANT, not caller
// freedom (BL-1462). BL-909 shipped this resolver and left every existing
// literal in place — `InvoiceDetailsModal`'s 70/80, `ReadyConfirmationModal`'s
// 9999, and eighteen others — so the alternative it replaced stayed reachable.
// That is what collided on the bidder-approval screen: the modal opened at 60
// and the `ConfirmDialog` inside it also passed 60, so the dialog's backdrop
// painted at 59 — BEHIND the panel it was meant to dim. White card on a white
// card, and the dialog on top only by DOM order.
//
// BL-1462 deleted all twenty. A call site outside this folder no longer states
// a layer, and `designsystem/no-overlay-zindex-literal` fails the build if one
// comes back. The escape hatch survives for a variant that genuinely needs a
// fixed number and says why on the line — never as the default way to raise
// something above what it opened from. If you are reaching for it to fix a
// stacking bug, the bug is that something above you is asserting a number too.

'use client';

import { createContext, useContext } from 'react';

/**
 * Layer of a top-level overlay — one with no overlay ancestor. Matches the
 * historical default so every un-nested overlay renders exactly where it did
 * before this module existed.
 */
export const OVERLAY_BASE_Z_INDEX = 50;

/**
 * Gap between consecutive overlay layers. Wider than 1 because each overlay
 * paints its backdrop at `zIndex - 1`; the spare room means a nested overlay's
 * backdrop still lands above the parent overlay's content rather than between
 * the parent's backdrop and its panel.
 */
export const OVERLAY_LAYER_STEP = 10;

/** Layer occupied by the nearest enclosing overlay; `null` at the page root. */
const OverlayLayerContext = createContext<number | null>(null);

/**
 * Resolve the z-index an overlay should render at.
 *
 * @param explicitZIndex - The call site's `zIndex` prop, if it passed one. An
 *   explicit value always wins. Since BL-1462 no feature call site passes one —
 *   it is reserved for a named variant that needs a fixed number and states why
 *   on the line, and `designsystem/no-overlay-zindex-literal` enforces that.
 * @returns The explicit value when supplied; otherwise one step above the
 *   enclosing overlay, or {@link OVERLAY_BASE_Z_INDEX} when there is none.
 */
export function useOverlayZIndex(explicitZIndex?: number): number {
  const enclosingLayer = useContext(OverlayLayerContext);
  if (explicitZIndex != null) return explicitZIndex;
  if (enclosingLayer == null) return OVERLAY_BASE_Z_INDEX;
  return enclosingLayer + OVERLAY_LAYER_STEP;
}

/**
 * Publish `zIndex` as the current layer to everything an overlay renders,
 * so overlays opened from within it stack above rather than behind.
 *
 * Every overlay primitive wraps its own `{children}` in this. Consumers never
 * render it directly.
 */
export function OverlayLayerProvider({
  zIndex,
  children,
}: {
  zIndex: number;
  children: React.ReactNode;
}) {
  return <OverlayLayerContext.Provider value={zIndex}>{children}</OverlayLayerContext.Provider>;
}

/**
 * Gap between an overlay and a PORTALED popover opened inside it.
 *
 * A popover that portals to `document.body` escapes its host overlay's
 * stacking context (every overlay panel sets a `transform`, which creates
 * one), so unlike an in-place child its number is a *global* claim and has to
 * be resolved rather than pinned.
 *
 * `1` is the whole available band, not an arbitrary small number: an overlay
 * nested inside the host paints its backdrop at `host + OVERLAY_LAYER_STEP - 1`
 * (= +9), so anything from +1 to +9 clears the host's panel while staying under
 * whatever is opened on top of it. +1 leaves the rest of the band free.
 */
export const POPOVER_LAYER_OFFSET = 1;

/**
 * Resolve the z-index a portaled popover, menu, or tooltip should render at.
 *
 * Use this instead of a hardcoded `z-[NN]` class or `zIndex: NN` style whenever
 * the element portals out of its host. Non-portaled children need nothing —
 * they are scoped to the host's stacking context and cannot collide with
 * another overlay.
 *
 * @returns One {@link POPOVER_LAYER_OFFSET} above the enclosing overlay's
 *   layer, or above {@link OVERLAY_BASE_Z_INDEX} when there is no overlay
 *   ancestor (the popover is on the page itself).
 */
export function usePopoverZIndex(): number {
  // Reads the enclosing layer DIRECTLY rather than going through
  // `useOverlayZIndex()`. That hook steps a full `OVERLAY_LAYER_STEP`, which is
  // right for a nested overlay (it needs the 10-wide gap for its own backdrop)
  // and wrong for a popover: stepping 10 then adding 1 lands the popover at
  // +11, i.e. ABOVE the band of any overlay opened on top of its host.
  const enclosingLayer = useContext(OverlayLayerContext);
  return (enclosingLayer ?? OVERLAY_BASE_Z_INDEX) + POPOVER_LAYER_OFFSET;
}

/** Attributes that take a closed eager overlay out of reach. See {@link closedOverlayA11yProps}. */
export interface ClosedOverlayA11yProps {
  inert: boolean;
  'aria-hidden': true | undefined;
}

/**
 * Attributes for the `role="dialog"` container of an EAGER overlay
 * (`SlideOutPanel`, `FullScreenModal`), which keeps its
 * subtree mounted and closes by opacity/transform alone (BL-1959).
 *
 * Closed, the container is `inert` AND `aria-hidden`. Both are needed:
 *
 *  - `inert` is what browsers and real assistive tech honour: no clicks, no
 *    focus, out of the tab order and out of the accessibility tree.
 *  - Playwright's `getByRole` and Testing Library's role queries ignore
 *    `inert` and read only `aria-hidden`. Without it a page with three closed
 *    ConfirmDialogs still answers `getByRole('button', { name: 'Confirm' })`
 *    with three ghosts, and a click on one hangs behind `pointer-events-none`.
 *
 * Open, both are omitted. They flip in the same commit as `isOpen`, before
 * `useFocusTrap`'s effect runs, so the focus hand-off on open and the restore
 * on close are unaffected. The mounted subtree (and any form state in it) is
 * untouched, and `data-state` still carries open/closed for automation.
 *
 * `Modal` renders nothing while closed and needs none of this.
 *
 * @param isOpen - Whether the overlay is open.
 * @returns Props to spread onto the overlay's `role="dialog"` container.
 */
export function closedOverlayA11yProps(isOpen: boolean): ClosedOverlayA11yProps {
  return { inert: !isOpen, 'aria-hidden': isOpen ? undefined : true };
}
